import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Container,
  Stack,
  Alert,
  AlertTitle
} from '@mui/material';
import { 
  Lock as LockIcon,
  Logout as LogoutIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { useAppAuth } from '../auth/appAuth';
import { useLocation } from 'react-router-dom';

const NoAccessPage: React.FC = () => {
  const { user, logout } = useAppAuth();
  const location = useLocation();
  
  // Try to get error details from navigation state
  const errorData = location.state?.errorData;
  const supportEmail = errorData?.support_contact || 'camilo.mendez@crant.ai';

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleContactSupport = () => {
    window.location.href = `mailto:${supportEmail}?subject=Access Request&body=Hello, I need access to the platform. My email is: ` + encodeURIComponent(user?.email || '');
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            width: '100%',
            textAlign: 'center'
          }}
        >
          <Stack spacing={3} alignItems="center">
            {/* Icon */}
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: 'warning.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2
              }}
            >
              <LockIcon sx={{ fontSize: 40, color: 'warning.dark' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" component="h1" gutterBottom>
              Access Not Found
            </Typography>

            {/* Alert with user's email */}
            <Alert severity="warning" sx={{ width: '100%' }}>
              <AlertTitle>Email Not Authorized</AlertTitle>
              The email address <strong>{user?.email}</strong> is not authorized to access this platform.
            </Alert>

            {/* Explanation */}
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center' }}>
              Your email address doesn't match our user records. If you believe this is an error, 
              please contact our support team to request access.
            </Typography>

            {/* Action Buttons */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: '100%', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={handleContactSupport}
                sx={{ flex: 1 }}
              >
                Contact Support
              </Button>
              
              <Button
                variant="contained"
                startIcon={<LogoutIcon />}
                onClick={handleLogout}
                sx={{ flex: 1 }}
              >
                Logout
              </Button>
            </Stack>

            {/* Support contact info */}
            <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 1, width: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Support Contact:</strong>
              </Typography>
              <Typography variant="body2" color="primary">
                {supportEmail}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
};

export default NoAccessPage;
